import React, { useMemo, useState } from 'react';
import { Icon } from '../../design/Icon';
import { Chip } from '../../components/shared/Badges';
import { MenuItem, MenuTitle, MenuSeparator, Popover } from '../../components/shared/Overlay';
import {
  DIRECTION_LABELS,
  LANGUAGE_LABELS,
  OUTCOME_LABELS,
  PRESET_LABELS,
  SENTIMENT_LABELS,
  SEVERITY_LABELS,
  SEVERITY_ORDER,
  severityText,
} from './reportsLogic';

const PRESETS = ['Last7Days', 'Last30Days', 'Last90Days', 'ThisMonth', 'PreviousMonth', 'ThisQuarter', 'PreviousQuarter'];
const entries = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

/** The filter kinds of the builder; `filterKey` matches the dataset descriptor's `filters`. */
function useFilterKinds({ topLevelName, topNodes, communities, categories, terms }) {
  return useMemo(
    () =>
      [
        { id: 'hierarchy', filterKey: 'hierarchyNodeIds', field: 'hierarchyNodeIds', label: topLevelName, options: topNodes.map((n) => ({ value: n.id, label: n.name })) },
        { id: 'community', filterKey: 'communityIds', field: 'communityIds', label: terms.community, options: communities.map((c) => ({ value: c.id, label: c.name })) },
        { id: 'dates', filterKey: 'dateRange', label: 'Dates' },
        categories && { id: 'category', filterKey: 'categoryIds', field: 'classificationCategoryIds', label: 'Classification', options: categories },
        { id: 'severity', filterKey: 'severities', field: 'severities', label: 'Severity', options: SEVERITY_ORDER.map((s) => ({ value: s, label: SEVERITY_LABELS[s] })) },
        { id: 'sentiment', filterKey: 'callerSentiments', field: 'callerSentiments', label: 'Caller sentiment', options: entries(SENTIMENT_LABELS) },
        { id: 'language', filterKey: 'languages', field: 'languages', label: 'Language', options: entries(LANGUAGE_LABELS) },
        { id: 'outcome', filterKey: 'outcomes', field: 'outcomes', label: 'Outcome', options: entries(OUTCOME_LABELS) },
        { id: 'direction', filterKey: 'directions', field: 'directions', label: 'Direction', options: entries(DIRECTION_LABELS) },
      ].filter(Boolean),
    [topLevelName, topNodes, communities, categories, terms]
  );
}

function valueText(kind, values) {
  if (kind.id === 'severity') return severityText(values);
  const labels = Object.fromEntries((kind.options || []).map((o) => [o.value, o.label]));
  const names = values.map((v) => labels[v] || v);
  return names.length > 2 ? `${names[0]} and ${names.length - 1} more` : names.join(', ');
}

/**
 * Step 3 of the builder: one chip per active filter (click to change, x to remove), "Add filter", and the sort chip.
 * report: { filter, dateRange, sortColumn, sortDescending }; onChange receives a patch of those fields.
 */
export function FilterChips({ report, filterKeys, columns, hierarchy, categories, onChange }) {
  const kinds = useFilterKinds({ ...hierarchy, categories });
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(null);
  const filter = report.filter || {};
  const allowed = kinds.filter((k) => !filterKeys || filterKeys.includes(k.filterKey));
  const active = allowed.filter((k) => (k.id === 'dates' ? true : (filter[k.field] || []).length > 0));
  const inactive = allowed.filter((k) => !active.includes(k));
  const close = () => {
    setOpen(null);
    setAdding(null);
  };

  const setValues = (kind, values) => onChange({ filter: { ...filter, [kind.field]: values } });
  const toggle = (kind, value) => {
    const current = filter[kind.field] || [];
    setValues(kind, current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
  };

  const editor = (kind) =>
    kind.id === 'dates' ? (
      <>
        <MenuTitle>Dates</MenuTitle>
        {PRESETS.map((preset) => (
          <MenuItem
            key={preset}
            checkable
            on={report.dateRange?.preset === preset}
            onClick={() => {
              onChange({ dateRange: { preset } });
              close();
            }}
          >
            {PRESET_LABELS[preset]}
          </MenuItem>
        ))}
      </>
    ) : (
      <>
        <MenuTitle>{kind.label}</MenuTitle>
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {kind.options.map((option) => (
            <MenuItem key={option.value} checkable on={(filter[kind.field] || []).includes(option.value)} onClick={() => toggle(kind, option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </div>
      </>
    );

  const sortable = columns || [];
  const sortColumn = sortable.find((c) => c.key === report.sortColumn);
  const sortText = !sortColumn
    ? 'Dataset order'
    : `${sortColumn.header}, ${
        sortColumn.type === 'DateTime' ? (report.sortDescending ? 'newest first' : 'oldest first') : sortColumn.type === 'Text' ? (report.sortDescending ? 'Z to A' : 'A to Z') : report.sortDescending ? 'largest first' : 'smallest first'
      }`;

  return (
    <div className="row-wrap">
      {active.map((kind) => {
        const text = kind.id === 'dates' ? PRESET_LABELS[report.dateRange?.preset] || PRESET_LABELS.Last30Days : valueText(kind, filter[kind.field]);
        return (
          <Popover
            key={kind.id}
            open={open === kind.id}
            onClose={close}
            width={240}
            label={`${kind.label} filter`}
            anchor={
              <Chip
                active
                keyLabel={kind.label}
                onClick={() => setOpen(open === kind.id ? null : kind.id)}
                onRemove={() => (kind.id === 'dates' ? onChange({ dateRange: { preset: 'Last30Days' } }) : setValues(kind, []))}
                removeLabel={`Remove ${kind.label} filter`}
              >
                {text}
              </Chip>
            }
          >
            {editor(kind)}
          </Popover>
        );
      })}
      <Popover
        open={open === '__add'}
        onClose={close}
        width={240}
        label="Add filter"
        anchor={
          <button type="button" className="chip" onClick={() => setOpen(open === '__add' ? null : '__add')} disabled={!inactive.length}>
            <Icon name="plus" className="ico-sm" />
            Add filter
          </button>
        }
      >
        {adding ? (
          editor(adding)
        ) : (
          <>
            <MenuTitle>Add filter</MenuTitle>
            {inactive.map((kind) => (
              <MenuItem key={kind.id} onClick={() => setAdding(kind)}>
                {kind.label}
              </MenuItem>
            ))}
          </>
        )}
      </Popover>
      <span className="sep" />
      <Popover
        open={open === '__sort'}
        onClose={close}
        width={260}
        label="Sort"
        anchor={
          <button type="button" className="chip" onClick={() => setOpen(open === '__sort' ? null : '__sort')}>
            <span className="k">Sort</span>
            {sortText}
          </button>
        }
      >
        <MenuTitle>Sort by</MenuTitle>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {sortable.map((column) => (
            <MenuItem key={column.key} checkable on={report.sortColumn === column.key} onClick={() => onChange({ sortColumn: column.key })}>
              {column.header}
            </MenuItem>
          ))}
        </div>
        <MenuSeparator />
        <MenuItem checkable on={Boolean(report.sortDescending)} onClick={() => onChange({ sortDescending: true })}>
          Descending (newest, largest, Z to A)
        </MenuItem>
        <MenuItem checkable on={!report.sortDescending} onClick={() => onChange({ sortDescending: false })}>
          Ascending (oldest, smallest, A to Z)
        </MenuItem>
      </Popover>
    </div>
  );
}
